import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Activity } from "lucide-react";
import { adminApi } from "@/lib/admin";

const HOUR_LABEL = "Last hour";
const DAY_LABEL = "Last 24h";

export default function EngineHealthSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.engineHealth(60);
      setData(r);
    } catch (err) {
      setError(err.message || "Couldn't load engine health.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 30s.
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) {
    return (
      <p
        data-testid="engine-health-loading"
        className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-3"
      >
        <Loader2 size={12} className="inline animate-spin mr-2" />
        Loading engine health…
      </p>
    );
  }
  if (error) {
    return (
      <p
        data-testid="engine-health-error"
        className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mb-3"
      >
        {error}
      </p>
    );
  }
  if (!data) return null;

  const hour = data.last_hour;
  const day = data.last_24h;
  const userKeys = data.user_keys || {};
  const sched = data.agent_scheduler || {};
  const health = data.health || "green";
  const healthTone = {
    green: { dot: "bg-emerald-400", label: "All systems nominal" },
    amber: { dot: "bg-yellow-400", label: "Degraded — see below" },
    red: { dot: "bg-red-400", label: "Critical — keys exhausted" },
  }[health] || { dot: "bg-slate-500", label: health };

  const formatCooldown = (s) => {
    if (!s) return "—";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div className="space-y-6" data-testid="engine-health-content">
      <div className="flex items-center justify-between">
        <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          Window: {HOUR_LABEL} · auto-refresh every 30s
        </div>
        <button
          onClick={load}
          data-testid="engine-health-refresh"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-1.5 transition-colors"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Health strip: pill + key pool + scheduler */}
      <div
        data-testid="engine-health-strip"
        className="border border-[var(--border)] p-4 grid md:grid-cols-3 gap-4"
      >
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
            System
          </div>
          <div className="flex items-center gap-2.5">
            <span
              data-testid={`engine-health-pill-${health}`}
              className={`w-2 h-2 rounded-full ${healthTone.dot} ${health === "green" ? "animate-pulse" : ""}`}
            />
            <span className="font-prose text-base text-[var(--text)] capitalize">
              {health}
            </span>
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1.5">
            {healthTone.label}
          </div>
        </div>
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
            Paid-key pool (live users)
          </div>
          <div className="font-prose text-base text-[var(--text)]" data-testid="health-keypool-available">
            {userKeys.available ?? "—"} / {userKeys.configured ?? "—"} ready
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1.5 truncate">
            {(userKeys.labels || []).map((label) => (
              <span
                key={label}
                data-testid={`health-key-${label}`}
                className={`mr-2 ${(userKeys.cooled_down || []).includes(label) ? "text-yellow-400" : "text-[var(--text-muted)]"}`}
              >
                {label}
                {(userKeys.cooled_down || []).includes(label) ? "·cool" : ""}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
            Agent scheduler
          </div>
          <div className="font-prose text-base text-[var(--text)]" data-testid="health-scheduler-state">
            {sched.master_paused
              ? "Paused (master)"
              : sched.cooldown_active
              ? `Cooldown ${formatCooldown(sched.cooldown_remaining_s)}`
              : sched.running
              ? "Running"
              : "Stopped"}
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1.5">
            tick {sched.tick_interval_s ?? "—"}s · {data.agents_today?.conversations ?? 0} today
          </div>
        </div>
      </div>

      {hour.total === 0 ? (
        <p
          data-testid="engine-health-empty"
          className="font-ui text-sm text-[var(--text-muted)]"
        >
          No chat traffic in the last hour. Send a few messages and refresh.
        </p>
      ) : (
        <>
          {/* Request breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile
              testid="health-primary-direct"
              label="Primary success"
              value={`${hour.primary_direct_pct ?? hour.gemini_direct_pct}%`}
              sub={`${hour.primary_success ?? hour.gemini_success} reqs`}
              tone="good"
            />
            <Tile
              testid="health-primary-retry"
              label="Retry success"
              value={`${hour.primary_retry_pct ?? hour.gemini_retry_pct}%`}
              sub={`${hour.primary_retry_success ?? hour.gemini_retry_success} reqs`}
              tone={(hour.primary_retry_success ?? hour.gemini_retry_success) > 0 ? "warn" : "muted"}
            />
            <Tile
              testid="health-fallback"
              label="Claude fallback"
              value={`${hour.fallback_pct}%`}
              sub={`${hour.claude_fallback} reqs`}
              tone={hour.fallback_pct > 5 ? "warn" : "muted"}
            />
            <Tile
              testid="health-served"
              label="Served"
              value={`${hour.served_pct}%`}
              sub={`${hour.total} reqs`}
              tone="good"
            />
            <Tile
              testid="health-failed"
              label="Failed"
              value={`${hour.failed_pct}%`}
              sub={`${hour.failed} reqs`}
              tone={hour.failed > 0 ? "bad" : "muted"}
            />
          </div>

          {/* Latency */}
          <div className="grid grid-cols-2 gap-3">
            <Tile
              testid="health-primary-latency"
              label="Primary p50 / p95"
              value={
                hour.latency_p?.primary?.n > 0
                  ? `${hour.latency_p.primary.p50}ms / ${hour.latency_p.primary.p95}ms`
                  : `${hour.avg_latency_ms.primary ?? hour.avg_latency_ms.gemini}ms`
              }
              sub={hour.latency_p?.primary?.n > 0 ? `n=${hour.latency_p.primary.n}` : ""}
              tone={(hour.latency_p?.primary?.p95 ?? 0) > 8000 ? "warn" : "muted"}
            />
            <Tile
              testid="health-claude-latency"
              label="Claude p50 / p95"
              value={
                hour.latency_p?.claude?.n > 0
                  ? `${hour.latency_p.claude.p50}ms / ${hour.latency_p.claude.p95}ms`
                  : hour.avg_latency_ms.claude > 0
                  ? `${hour.avg_latency_ms.claude}ms`
                  : "—"
              }
              sub={hour.latency_p?.claude?.n > 0 ? `n=${hour.latency_p.claude.n}` : ""}
              tone="muted"
            />
          </div>

          {/* Fallback reasons */}
          {Object.keys(hour.fallback_reasons).length > 0 && (
            <div data-testid="health-reasons">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
                Fallback reasons
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(hour.fallback_reasons).map(([reason, n]) => (
                  <div
                    key={reason}
                    data-testid={`health-reason-${reason}`}
                    className="border border-[var(--border)] px-3 py-2 flex items-center justify-between"
                  >
                    <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)]">
                      {reason === "503"
                        ? "503 high demand"
                        : reason === "429"
                        ? "429 quota"
                        : reason === "timeout"
                        ? "Timeout"
                        : reason === "network"
                        ? "Network"
                        : reason === "5xx"
                        ? "5xx other"
                        : reason}
                    </span>
                    <span className="font-prose text-lg text-[var(--text)]">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 24h totals */}
          <div className="border-t border-[var(--border)] pt-5">
            <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-3 flex items-center gap-2">
              <Activity size={11} /> {DAY_LABEL} totals
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono-ui text-[10px] tracking-[0.18em] uppercase">
              <Mini label="Total" value={day.total} testid="health-day-total" />
              <Mini label="Primary direct" value={day.primary_success ?? day.gemini_success} testid="health-day-primary-success" />
              <Mini label="Primary retry" value={day.primary_retry_success ?? day.gemini_retry_success} testid="health-day-primary-retry" />
              <Mini label="Claude fallback" value={day.claude_fallback} testid="health-day-claude-fallback" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone = "muted", testid }) {
  const accentClass = {
    good: "text-[var(--accent)]",
    warn: "text-yellow-400",
    bad: "text-red-400",
    muted: "text-[var(--text)]",
  }[tone];
  return (
    <div
      data-testid={testid}
      className="border border-[var(--border)] p-3 flex flex-col gap-1"
    >
      <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        {label}
      </span>
      <span className={`font-prose text-2xl leading-none ${accentClass}`}>{value}</span>
      {sub && (
        <span className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
          {sub}
        </span>
      )}
    </div>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between border border-[var(--border)] px-3 py-2">
      <span className="text-[var(--text-dim)]">{label}</span>
      <span className="text-[var(--text)]">{value}</span>
    </div>
  );
}
